import { useRef } from "react";

import { Api } from "@noom/noomscape";

import { useAppContext } from "@/contexts";
import { RetriageEligibility } from "@/models";
import { isB2CPartner } from "@/utils/partners";
import { captureException } from "@/utils/sentry";

import { useSaveTriageData } from "./useSaveTriageData";
import { useUpdateLastTriageTimestamp } from "./useUpdateLastTriageTimestamp";

export const RECORD_WAIT_CAP_MS = 2000;

export type RetriageOutcome = "KEPT_SAME_OFFERING" | "RECENTLY_RETRIAGED";

/**
 * Saves the retriage survey and reports the outcome to backend with the saved
 * survey key. Runs once per retriage attempt (one eligibility check), even
 * across remounts. A failed save is not retried, so the outcome is never
 * reported twice.
 */
export const useRecordRetriageOutcome = () => {
  const {
    partnerInfo,
    pendingRetriageOutcomeRef,
    retriageEligibility,
    retriageOutcomeRecordedFor,
    setRetriageOutcomeRecordedFor,
    surveyAnswers,
  } = useAppContext();
  const { saveTriageData } = useSaveTriageData(partnerInfo?.id);
  const { updateLastTriageTimestamp } = useUpdateLastTriageTimestamp();
  // Covers repeat calls before the context update lands
  const recordingForRef = useRef<RetriageEligibility>();

  const saveRetriageSurvey = async () => {
    try {
      return await saveTriageData(surveyAnswers, "COMPLETE", "RETRIAGE");
    } catch (e) {
      captureException(e);
      return undefined;
    }
  };

  const recordRecentlyRetriagedAttempt = (
    accessCode: string,
    dppTriageKey: string | undefined,
  ) =>
    Api.call(
      "retriage.recordRecentlyRetriagedAttempt",
      Api.api.retriage.recordRecentlyRetriagedAttempt,
      { accessCode, ...(!!dppTriageKey && { dppTriageKey }) },
    ).catch((e: unknown) => {
      captureException(e);
    });

  const reportOutcome = async (
    accessCode: string,
    outcome: RetriageOutcome,
  ) => {
    const dppTriageKey = await saveRetriageSurvey();

    // Never send RECENTLY_RETRIAGED to updateLastTriageTimestamp; it would
    // reset the cooldown on backends that don't know that result
    if (outcome === "RECENTLY_RETRIAGED") {
      await recordRecentlyRetriagedAttempt(accessCode, dppTriageKey);
      return;
    }
    await updateLastTriageTimestamp(accessCode, outcome, dppTriageKey);
  };

  const recordRetriageOutcome = (
    accessCode: string,
    outcome: RetriageOutcome,
  ): Promise<void> => {
    if (
      isB2CPartner(partnerInfo) ||
      !retriageEligibility ||
      recordingForRef.current === retriageEligibility ||
      retriageOutcomeRecordedFor === retriageEligibility
    ) {
      return Promise.resolve();
    }
    recordingForRef.current = retriageEligibility;
    setRetriageOutcomeRecordedFor(retriageEligibility);

    const record = reportOutcome(accessCode, outcome);
    pendingRetriageOutcomeRef.current = record;
    const clearPending = () => {
      if (pendingRetriageOutcomeRef.current === record) {
        pendingRetriageOutcomeRef.current = undefined;
      }
    };
    record.then(clearPending, clearPending);
    return record;
  };

  /**
   * Waits, at most RECORD_WAIT_CAP_MS, for an in-flight record so leaving the
   * page doesn't drop the outcome.
   */
  const waitForRetriageOutcome = async () => {
    const pending = pendingRetriageOutcomeRef.current;
    if (!pending) {
      return;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const cap = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, RECORD_WAIT_CAP_MS);
    });
    await Promise.race([pending.catch(() => undefined), cap]);
    clearTimeout(timer);
  };

  return { recordRetriageOutcome, waitForRetriageOutcome };
};
